import React, { useContext, useMemo, useState } from "react";
import { createTheme, ThemeProvider, useTheme } from "@material-ui/core/styles";
import {
  IconButton,
  Tooltip,
  Badge,
  useMediaQuery
} from "@material-ui/core";
import {
  MoreVert,               // Três pontinhos verticais
  CheckCircle,            // Check único sólido com círculo
  Cancel,
  Reply                   // Seta de resposta melhorada para voltar
} from "@material-ui/icons";

import { green } from '@material-ui/core/colors';
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import AcceptTicketButton from "./AcceptTicketButton";
import ReopenTicketButton from "./ReopenTicketButton";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import RegisterLossModal from "../RegisterLossModal";
import {
  getOutcomeBoardsFromTicket,
  getPrimaryOutcomeBoardId,
  hasOutcomeTrackingTicket
} from "../../utils/commercialOutcome";
import invalidateDashboardCache from "../../utils/invalidateDashboardCache";
import { useCommercialContext } from "../../context/Commercial/CommercialContext";
import CommercialTicketActions from "../CommercialTicketActions";
import useStyles from "./styles";
import { isProbablyIpad } from "../../utils/orientationLock";

// Traduções em português devem estar no arquivo pt.js:
// "messagesList.header.buttons.reopen": "Reabrir",
// "messagesList.header.buttons.accept": "Aceitar",
// "messagesList.header.buttons.return": "Retornar",
// "messagesList.header.buttons.resolve": "Resolver",
// "messagesList.header.buttons.options": "Opções",



const TicketActionButtonsCustom = ({ ticket }) => {
  const commercial = useCommercialContext();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const returnInMenu = isMobile || (typeof window !== "undefined" && isProbablyIpad(window));
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lossModalOpen, setLossModalOpen] = useState(false);
  const ticketOptionsMenuOpen = Boolean(anchorEl);
  const { user } = useContext(AuthContext);
  const { setCurrentTicket } = useContext(TicketsContext);
  const ticketId = ticket?.id;
  const outcomeBoards = useMemo(() => getOutcomeBoardsFromTicket(ticket), [ticket]);
  const hasOutcomeTracking = hasOutcomeTrackingTicket(ticket);
  const primaryOutcomeBoardId = getPrimaryOutcomeBoardId(ticket);

  const customTheme = createTheme({
    palette: {
      primary: green,
    },
  });

  const handleOpenTicketOptionsMenu = e => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseTicketOptionsMenu = e => {
    setAnchorEl(null);
  };

  const handleUpdateTicketStatus = async (e, status, userId) => {
    setLoading(true);
    try {
      const { data: updatedTicket } = await api.put(`/tickets/${ticket.id}`, {
        status,
        userId: userId || null,
        useIntegration: status === "closed" ? false : ticket.useIntegration,
        promptId: status === "closed" ? false : ticket.promptId,
        integrationId: status === "closed" ? false : ticket.integrationId
      });

      const ticketData = updatedTicket || { ...ticket, status };

      if (status === "open") {
        invalidateDashboardCache();
      }

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("ticket:patched", { detail: { ticket: ticketData } })
        );
      }

      if (status === "open") {
        setCurrentTicket({ ...ticketData, code: "#open" });

        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("tickets:change-tab", {
              detail: { tab: "open", ticket: ticketData }
            })
          );
        }
      } else {
        setCurrentTicket(prev => {
          if (!prev || prev.id !== ticketData.id) {
            return prev;
          }
          return { ...ticketData, code: "#closed" };
        });
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveOutcome = async (outcome, extra = {}, options = {}) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/resolve`, {
        outcome,
        archive: true,
        ...(primaryOutcomeBoardId ? { kanbanBoardId: primaryOutcomeBoardId } : {}),
        ...extra
      });

      const ticketData = data?.ticket || { ...ticket, status: "closed", outcome };

      invalidateDashboardCache();

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("ticket:patched", { detail: { ticket: ticketData } })
        );
      }

      setCurrentTicket(prev => {
        if (!prev || prev.id !== ticketData.id) {
          return prev;
        }
        return { ...ticketData, code: "#closed" };
      });
    } catch (err) {
      toastError(err);
      if (options.rethrow) {
        throw err;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmLoss = async ({ kanbanBoardId, lossReasonId }) => {
    await handleResolveOutcome("lost", { kanbanBoardId, lossReasonId }, { rethrow: true });
  };
  const mobileOutcomeItems = isMobile && hasOutcomeTracking && (ticket.status === "open" || ticket.status === "pending")
    ? [
      ...(ticket.status === "open" ? [{ key: "won", label: "Ganho", disabled: loading, onClick: () => handleResolveOutcome("won") }] : []),
      { key: "lost", label: "Perda", disabled: loading, onClick: () => setLossModalOpen(true) },
    ] : isMobile && !hasOutcomeTracking && (ticket.status === "open" || ticket.status === "pending")
      ? [{ key: "finish", label: "Finalizar", disabled: loading, onClick: () => handleUpdateTicketStatus(null, "closed", user?.id) }] : [];

  const renderClosedTicketButtons = () => (
    <ReopenTicketButton
      compact={isMobile}
      loading={loading}
      onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
    />
  );

  const renderPendingTicketButtons = () => (
    <>
      <AcceptTicketButton
        compact={isMobile}
        loading={loading}
        style={{ marginRight: 8 }}
        onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
      />

      {!isMobile && <ButtonWithSpinner
        loading={loading}
        size="small"
        variant="contained"
        className={classes.actionButton}
        onClick={e => {
          if (hasOutcomeTracking) {
            setLossModalOpen(true);
            return;
          }
          handleUpdateTicketStatus(e, "closed", user?.id);
        }}
      >
        {hasOutcomeTracking ? "Perda" : "Finalizar"}
      </ButtonWithSpinner>}
      {isMobile && <Tooltip title={i18n.t("messagesList.header.buttons.options")} classes={{ tooltip: classes.tooltips }}>
        <IconButton onClick={handleOpenTicketOptionsMenu} aria-label={i18n.t("messagesList.header.buttons.options")} className={`${classes.optionsButton} ${classes.iconButton} ${classes.mobileTightHeaderActions}`} size="small"><MoreVert /></IconButton>
      </Tooltip>}
    </>
  );

  const renderOpenTicketButtons = () => (
    <>
      {hasOutcomeTracking && !isMobile && (
        <>
          <Tooltip title="Ganho" classes={{ tooltip: classes.tooltips }}>
            <IconButton
              onClick={e => handleResolveOutcome("won")}
              color="primary"
              disabled={loading}
              className={`${classes.resolveButton} ${classes.iconButton} ${classes.mobileTightHeaderActions}`}
              size="small"
            >
              <CheckCircle />
            </IconButton>
          </Tooltip>
          <Tooltip title="Perda" classes={{ tooltip: classes.tooltips }}>
            <IconButton
              onClick={() => setLossModalOpen(true)}
              color="primary"
              disabled={loading}
              className={`${classes.resolveButton} ${classes.iconButton} ${classes.mobileTightHeaderActions}`}
              size="small"
            >
              <Cancel />
            </IconButton>
          </Tooltip>
        </>
      )}
      {!returnInMenu && <Tooltip
        title={i18n.t("messagesList.header.buttons.return")}
        classes={{ tooltip: classes.tooltips }}
      >
        <IconButton
          onClick={e => handleUpdateTicketStatus(e, "pending", null)}
          aria-label={i18n.t("messagesList.header.buttons.return")}
          className={`${classes.returnButton} ${classes.iconButton} ${classes.mobileTightHeaderActions}`}
          disabled={loading}
          size="small"
        >
          <Reply />
        </IconButton>
      </Tooltip>}

      {!hasOutcomeTracking && !isMobile && (
        <ThemeProvider theme={customTheme}>
          <Tooltip
            title={i18n.t("messagesList.header.buttons.resolve")}
            classes={{ tooltip: classes.tooltips }}
          >
            <IconButton
              onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
              color="primary"
              disabled={loading}
              className={`${classes.resolveButton} ${classes.iconButton} ${classes.mobileTightHeaderActions}`}
              size="small"
            >
              <CheckCircle />
            </IconButton>
          </Tooltip>
        </ThemeProvider>
      )}

      <Tooltip
        title={i18n.t("messagesList.header.buttons.options")}
        classes={{ tooltip: classes.tooltips }}
      >
        <IconButton
          onClick={handleOpenTicketOptionsMenu}
          aria-label={i18n.t("messagesList.header.buttons.options")}
          className={`${classes.optionsButton} ${classes.iconButton} ${classes.mobileTightHeaderActions}`}
          size="small"
        >
          <MoreVert />
        </IconButton>
      </Tooltip>

    </>
  );

  if (commercial?.enabled) return <CommercialTicketActions ticket={ticket} onTicketOpened={updatedTicket => {
    invalidateDashboardCache();
    setCurrentTicket({ ...updatedTicket, code: '#open' });
    window.dispatchEvent(new CustomEvent('tickets:change-tab', { detail: { tab: 'open', ticket: updatedTicket } }));
  }} />;
  return (
    <>
      <div className={classes.actionButtonsContainer}>
        <div className={classes.actionButtons}>
          {ticket.status === "closed" && renderClosedTicketButtons()}
          {ticket.status === "open" && renderOpenTicketButtons()}
          {ticket.status === "pending" && renderPendingTicketButtons()}
        </div>
      </div>
      {(ticket.status === "open" || (ticket.status === "pending" && mobileOutcomeItems.length > 0)) && <TicketOptionsMenu
        ticket={ticket}
        anchorEl={anchorEl}
        menuOpen={ticketOptionsMenuOpen}
        handleClose={handleCloseTicketOptionsMenu}
        onReturnToQueue={ticket.status === "open" ? () => handleUpdateTicketStatus(null, "pending", null) : undefined}
        returnInMenu={returnInMenu}
        outcomeItems={mobileOutcomeItems}
        outcomeOnly={ticket.status === "pending"}
        disableMutations={loading}
      />}
      <RegisterLossModal
        open={lossModalOpen}
        onClose={() => setLossModalOpen(false)}
        ticketId={ticketId}
        boardId={primaryOutcomeBoardId}
        boards={outcomeBoards}
        onConfirm={handleConfirmLoss}
      />
    </>
  );
};

export default TicketActionButtonsCustom;
